import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaEyeSlash, FaSearch } from 'react-icons/fa';
import { 
  TextField, 
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Container, Header, HeaderLeft, Title, Content, BackButton, HeaderButton, ControlsContainer, MainContainer } from '../../common/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import { useAlert } from '../../../contexts/AlertContext';
import { organizationApi } from '../../../services/api';
import OrgUnitTypeSelector from '../../common/OrgUnitTypeSelector';
import EmployeeSearch from '../../common/EmployeeSearch';
import ClusterSelector from '../../common/ClusterSelector';
import { EmployeeAvatar } from '../../common/EmployeeAvatar';
import EmptyState from '../../common/EmptyState';
import { formatDateOnly } from '../../../utils/dateOnly';

const BusinessUnitsConfiguration = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [showControls, setShowControls] = useState(true);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState('');
  const [selectedOrgUnitType, setSelectedOrgUnitType] = useState('');
  // ADR-120: 'operating' (Active + Ending, the default) | 'inactive' | 'all'
  const [lifecycleFilter, setLifecycleFilter] = useState('operating');
  const [isLoading, setIsLoading] = useState(true);

  const fetchBusinessUnits = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getBusinessUnits({ includeInactive: true });
      setBusinessUnits(data);
    } catch (err) {
      showAlert('Failed to load business units.', 'error', err);
      console.error('Error fetching business units:', err);
    } finally {
      setIsLoading(false);
    }
  };


  useEffect(() => {
    fetchBusinessUnits();
  }, []);


  const renderClusterOptions = (clusters, parentChain = []) => {
    return clusters.map(cluster => {
      return [
        <MenuItem 
          key={cluster.id} 
          value={cluster.id}
          sx={{
            pl: cluster.level ? `${cluster.level * 20 + 16}px` : '16px',
            borderLeft: cluster.level ? `${cluster.level}px solid #f3f4f6` : 'none'
          }}
        >
          {cluster.level > 0 && '└ '}
          {cluster.name}
        </MenuItem>,
        ...renderClusterOptions(cluster.children || [], [...parentChain, cluster.id])
      ];
    }).flat();
  };

  const filteredBusinessUnits = businessUnits.filter(unit => {
    const matchesSearch = unit.name.toLowerCase().includes((searchTerm || '').toLowerCase());
    const matchesOwner = !selectedOwner || unit.owner?.id === selectedOwner.id;
    const matchesCluster = !selectedCluster || unit.cluster?.id === selectedCluster;
    const matchesOrgUnitType = !selectedOrgUnitType || unit.orgUnitType?.id === selectedOrgUnitType;
    const matchesLifecycle = lifecycleFilter === 'all'
      || (lifecycleFilter === 'inactive' ? unit.status === 'Inactive' : unit.status !== 'Inactive');
    return matchesSearch && matchesOwner && matchesCluster && matchesOrgUnitType && matchesLifecycle;
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/organization')}>
            <FaArrowLeft /> Back to Organization
          </BackButton>
          <Title>Business Units</Title>
        </HeaderLeft>
        <HeaderButton onClick={() => setShowControls(!showControls)}>
          {showControls ? <FaEyeSlash /> : <FaEye />}
          {showControls ? 'Hide Panels' : 'Show Panels'}
        </HeaderButton>
      </Header>

      <Content>
        <div style={{ flex: 1 }}>
          <ControlsContainer $show={showControls} style={{ marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search Business Unit"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }
              }}
              sx={{
                minWidth: '300px',
                flex: 1,
              }}
            />

            <EmployeeSearch
              value={selectedOwner}
              onChange={setSelectedOwner}
              label="Filter by Owner"
              placeholder="Select an owner..."
              sx={{ minWidth: 200, flex: 1 }}
              size="small"
            />

            <ClusterSelector
              value={selectedCluster}
              onChange={setSelectedCluster}
              label="Filter by Cluster"
              required={false}
              allowSelectAll={true}
              allOptionLabel="All Clusters"
              allOptionValue=""
              sx={{ minWidth: 200, flex: 1 }}
              size="small"
              showStructureType // ADR-119
            />

            <OrgUnitTypeSelector
              value={selectedOrgUnitType}
              onChange={setSelectedOrgUnitType}
              label="Filter by Organizational Unit Type"
              required={false}
              showEmptyOption={true}
              emptyOptionLabel="All Types"
              sx={{ minWidth: 200, flex: 1 }}
              size="small"
            />

            {/* ADR-120 */}
            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select value={lifecycleFilter} label="Status" onChange={(e) => setLifecycleFilter(e.target.value)}>
                <MenuItem value="operating">Operating</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
          </ControlsContainer>

          <MainContainer $show={showControls}>
            <div style={{ 
              width: '100%',
              height: '100%',
              overflowY: 'auto',
              padding: '24px'
            }}>
              {filteredBusinessUnits.length === 0 ? (
                <EmptyState
                  title="No Business Units Found"
                  description="No business units found matching the current filters. Try adjusting your search criteria or filters."
                />
              ) : (
                <Grid 
                  container 
                  spacing={3}
                  sx={{
                    width: '100%',
                    margin: '0 auto',
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(3, 1fr)',
                      lg: 'repeat(4, 1fr)',
                      xl: 'repeat(4, 1fr)'
                    },
                    gap: '24px',
                    '& .MuiGrid-item': {
                      padding: '0',
                      width: '100%'
                    }
                  }}
                >
                  {filteredBusinessUnits.map((businessUnit) => (
                    <Grid item key={businessUnit.id}>
                      <Card 
                        sx={{ 
                          width: '100%',
                          minHeight: '160px',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.2s',
                          border: '1px solid #e0e0e0',
                          boxShadow: 'none',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          backgroundColor: !businessUnit.parentBu ? '#EFF6FF' : '#f8f9fa',
                          '&:hover': {
                            borderColor: '#bdbdbd',
                            backgroundColor: !businessUnit.parentBu ? '#DBEAFE' : '#f1f3f5',
                          }
                        }}
                      >
                        <CardContent sx={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          padding: '16px !important',
                          flexGrow: 1,
                          position: 'relative'
                        }}>
                          {businessUnit.orgUnitType && (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6B7280',
                                fontSize: '11px',
                                backgroundColor: '#F3F4F6',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                width: 'fit-content'
                              }}
                            >
                              {businessUnit.orgUnitType.name}
                            </Typography>
                          )}

                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500,
                              color: '#212529',
                              marginBottom: '4px',
                              paddingRight: businessUnit.orgUnitType ? '100px' : '0' // Add padding to avoid overlap with type badge
                            }}
                          >
                            {businessUnit.name}
                          </Typography>

                          {businessUnit.status && businessUnit.status !== 'Active' && (
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '11px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                width: 'fit-content',
                                marginBottom: '4px',
                                color: businessUnit.status === 'Ending' ? '#B45309' : '#6B7280',
                                backgroundColor: businessUnit.status === 'Ending' ? '#FEF3C7' : '#E5E7EB'
                              }}
                            >
                              {businessUnit.status === 'Ending' ? `Ends ${formatDateOnly(businessUnit.endDate)}` : `Ended ${formatDateOnly(businessUnit.endDate)}`}
                            </Typography>
                          )}

                          {businessUnit.parentBu ? (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                marginBottom: '8px'
                              }}
                            >
                              part of {businessUnit.parentBu.name}
                            </Typography>
                          ) : (
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                fontSize: '12px',
                                fontStyle: 'italic',
                                marginBottom: '8px'
                              }}
                            >
                              Root business unit
                            </Typography>
                          )}

                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            marginBottom: '4px'
                          }}>
                            <EmployeeAvatar employee={businessUnit.owner} />
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: '#6c757d',
                                fontSize: '12px',
                                fontStyle: businessUnit?.owner?.fullName ? 'normal' : 'italic'
                              }}
                            >
                              Owner: {businessUnit?.owner?.fullName || 'Not assigned'}
                            </Typography>
                          </Box>

                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '12px'
                            }}
                          >
                            Cluster: {businessUnit.cluster?.name || 'No cluster assigned'}
                            {/* ADR-119 / ADR-122 §10.2: the cluster's Organizational Structure
                                Type as a muted code, full name on hover. Absent when untyped. */}
                            {businessUnit.cluster?.orgStructureType?.code && (
                              <Typography
                                component="span"
                                variant="caption"
                                title={businessUnit.cluster.orgStructureType.name}
                                sx={{ ml: 0.75, color: 'text.secondary', fontFamily: 'monospace' }}
                              >
                                {businessUnit.cluster.orgStructureType.code}
                              </Typography>
                            )}
                          </Typography>

                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: '#6c757d',
                              fontSize: '12px',
                              fontStyle: businessUnit.projectCount === 0 ? 'italic' : 'normal',
                              display: !businessUnit.parentBu ? 'none' : 'block'
                            }}
                          >
                            {businessUnit.projectCount === 0 
                              ? 'No projects assigned' 
                              : `${businessUnit.projectCount} ${businessUnit.projectCount === 1 ? 'project' : 'projects'} assigned`}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </div>
          </MainContainer>
        </div>
      </Content>
    </Container>
  );
};

export default BusinessUnitsConfiguration;